/**
 * Start a full camera test and follow it to the end (ENG-2457).
 *
 * On mount it loads the site's latest test, if any (and keeps following it
 * if it is still running). `start()` asks admin for a new run, then polls it
 * every 3 s until it is done, failed or timed out. Polling stops on unmount
 * and when the site changes.
 *
 * Every "no" the backend can give comes back as `notice`, for the UI to say
 * in words: a test already running (409), the hourly limit (429), the dry run
 * not switched on yet (503 — `unavailable` then stays true for the session,
 * and the connector's own report keeps showing).
 */
import React from "react";
import {
  fetchCameraDryRun,
  fetchLatestCameraDryRun,
  startCameraDryRun,
} from "./siteConnectivityApi";
import {
  ConnectivityResult,
  FINISHED_RUN_STATUSES,
  StartDryRunRequest,
} from "./siteConnectivityTypes";

export const DRY_RUN_POLL_MS = 3000;
/** The Job is killed after 10 minutes; stop asking a while after that. */
export const DRY_RUN_MAX_POLL_MS = 15 * 60 * 1000;
/** Consecutive failed polls before giving up. */
export const DRY_RUN_MAX_POLL_ERRORS = 5;

export type DryRunNotice =
  | { kind: "busy" }
  | { kind: "rate_limited"; retryAt: string | null }
  | { kind: "cluster_busy"; retryAt: string | null }
  | { kind: "forbidden"; message: string }
  | { kind: "error"; message: string };

export interface CameraDryRunState {
  /** The latest run's result, live while it runs. */
  result: ConnectivityResult | null;
  /** True from the click until the run finishes. */
  running: boolean;
  /** The dry run isn't available on this environment yet. */
  unavailable: boolean;
  /** The full test doesn't cover this site (fleet sites, 400 `not_supported`). */
  notSupported: boolean;
  notice: DryRunNotice | null;
  /** Loading the site's previous run. */
  loadingLatest: boolean;
  start: (options?: Omit<StartDryRunRequest, "site_id">) => Promise<void>;
  clearNotice: () => void;
}

export const useCameraDryRun = (
  siteId: number | null,
  { loadLatest = true }: { loadLatest?: boolean } = {}
): CameraDryRunState => {
  const [result, setResult] = React.useState<ConnectivityResult | null>(null);
  const [activeRunId, setActiveRunId] = React.useState<string | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [unavailable, setUnavailable] = React.useState(false);
  const [notSupported, setNotSupported] = React.useState(false);
  const [notice, setNotice] = React.useState<DryRunNotice | null>(null);
  const [loadingLatest, setLoadingLatest] = React.useState(false);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A different site starts from nothing.
  React.useEffect(() => {
    setResult(null);
    setActiveRunId(null);
    setNotice(null);
    setUnavailable(false);
    setNotSupported(false);
    if (siteId == null || !loadLatest) return;
    let cancelled = false;
    setLoadingLatest(true);
    fetchLatestCameraDryRun(siteId).then((outcome) => {
      if (cancelled) return;
      setLoadingLatest(false);
      if (outcome.kind === "ok") {
        setResult(outcome.data);
        if (
          outcome.data.run_id &&
          !FINISHED_RUN_STATUSES.has(outcome.data.status)
        ) {
          setActiveRunId(outcome.data.run_id);
        }
      } else if (outcome.kind === "unavailable") {
        setUnavailable(true);
      }
      // not_found: never tested. Errors here aren't worth a banner: the
      // button still works and says what happens when pressed.
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, loadLatest]);

  // Follow the active run until it finishes.
  React.useEffect(() => {
    if (!activeRunId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let errors = 0;
    const startedAt = Date.now();
    const poll = async () => {
      const outcome = await fetchCameraDryRun(activeRunId);
      if (cancelled) return;
      if (outcome.kind === "ok") {
        errors = 0;
        setResult(outcome.data);
        if (FINISHED_RUN_STATUSES.has(outcome.data.status)) {
          setActiveRunId(null);
          return;
        }
      } else if (outcome.kind === "unavailable") {
        setUnavailable(true);
        setActiveRunId(null);
        return;
      } else {
        errors += 1;
        if (errors >= DRY_RUN_MAX_POLL_ERRORS || outcome.kind === "not_found") {
          setNotice({
            kind: "error",
            message:
              "We lost track of the camera test. Its results will show here when you reopen this page.",
          });
          setActiveRunId(null);
          return;
        }
      }
      if (Date.now() - startedAt > DRY_RUN_MAX_POLL_MS) {
        setNotice({
          kind: "error",
          message:
            "The camera test is taking much longer than it should. Reopen this page later to see its results.",
        });
        setActiveRunId(null);
        return;
      }
      timer = setTimeout(poll, DRY_RUN_POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeRunId]);

  const start = React.useCallback(
    async (options: Omit<StartDryRunRequest, "site_id"> = {}) => {
      if (siteId == null) return;
      setNotice(null);
      setStarting(true);
      const outcome = await startCameraDryRun({ site_id: siteId, ...options });
      if (!mounted.current) return;
      setStarting(false);
      switch (outcome.kind) {
        case "ok":
          setResult(null);
          setActiveRunId(outcome.data.run_id);
          return;
        case "unavailable":
          setUnavailable(true);
          return;
        case "busy":
          setNotice({ kind: "busy" });
          // Follow the run that's already going, when admin says which.
          if (outcome.runId) setActiveRunId(outcome.runId);
          return;
        case "rate_limited":
          setNotice({ kind: "rate_limited", retryAt: outcome.retryAt });
          return;
        case "cluster_busy":
          setNotice({ kind: "cluster_busy", retryAt: outcome.retryAt });
          return;
        case "not_supported":
          setNotSupported(true);
          return;
        case "rejected":
          setNotice({ kind: "error", message: outcome.message });
          return;
        case "forbidden":
          setNotice({ kind: "forbidden", message: outcome.message });
          return;
        case "not_found":
          setUnavailable(true);
          return;
        default:
          setNotice({ kind: "error", message: outcome.message });
      }
    },
    [siteId]
  );

  return {
    result,
    running: starting || activeRunId != null,
    unavailable,
    notSupported,
    notice,
    loadingLatest,
    start,
    clearNotice: () => setNotice(null),
  };
};
